import { useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components';
import type { FundAccountStatementPageModel } from '../../hooks/useFundAccountStatementPage';
import { FundAccountStatementBody } from '../DealerFinancials/FundStatementPeriod/FundAccountStatementBody';
import type { RenderLine } from '../DealerFinancials/FundStatementPeriod/PeriodStatementBody';
import { PeriodHeader } from '../DealerFinancials/FundStatementPeriod/PeriodHeader';
import { StatementLineDisclosure } from './StatementLineDisclosure';
import { StatementLinePostings } from './StatementLinePostings';

type Props = Pick<
    FundAccountStatementPageModel,
    | 'header'
    | 'title'
    | 'subtitle'
    | 'state'
    | 'periodLabel'
    | 'datesLine'
    | 'statement'
    | 'layout'
    | 'onRetry'
    | 'selection'
    | 'period'
    | 'todayIso'
    | 'lines'
>;

export function FundAccountStatementView({
    header,
    title,
    subtitle,
    datesLine,
    selection,
    period,
    todayIso,
    lines,
    ...body
}: Props) {
    const { statement, periodLabel, onRetry } = body;
    const renderLine = useCallback<RenderLine>(
        (line, block) => (
            <StatementLineDisclosure
                line={line}
                split={block.split}
                open={lines.isOpen(line.key)}
                onToggle={() => lines.onToggle(line.key)}
                currency={statement?.currency ?? 'GBP'}
            >
                {statement && (
                    <StatementLinePostings
                        selection={selection}
                        period={period}
                        todayIso={todayIso}
                        periodLabel={periodLabel}
                        line={line}
                        window={{ from: statement.from, to: statement.to }}
                        currency={statement.currency}
                        page={lines.pageOf(line.key)}
                        block={block}
                        onPageChange={(page) => lines.onPageChange(line.key, page)}
                        onReloadStatement={onRetry}
                    />
                )}
            </StatementLineDisclosure>
        ),
        [lines, statement, selection, period, todayIso, periodLabel, onRetry],
    );

    return (
        <div className="space-y-6">
            <PeriodHeader {...header} />
            <Card ariaLabelledBy="fund-account-statement-heading">
                <CardHeader>
                    <CardTitle id="fund-account-statement-heading">{title}</CardTitle>
                    {datesLine && <p className="text-sm text-mp-muted">{datesLine}</p>}
                    <p className="text-sm text-mp-muted">{subtitle}</p>
                </CardHeader>
                <CardContent>
                    <FundAccountStatementBody {...body} renderLine={renderLine} />
                </CardContent>
            </Card>
        </div>
    );
}
